import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { NotificationItem } from '../../notification-item';

@Injectable({
  providedIn: 'root'
})
export class LatestExportsService {
  private readonly STORAGE_KEY = 'admin_ui_latest_exports';
  private readonly MAX_EXPORTS = 10;

  private readonly _latestExportsSubject = new BehaviorSubject<NotificationItem[]>([]);
  public latestExports$: Observable<NotificationItem[]> = this._latestExportsSubject.asObservable();

  private readonly _openLatestExportsPanel = new BehaviorSubject<boolean>(false);
  public openLatestExportsPanel$: Observable<boolean> = this._openLatestExportsPanel.asObservable();

  loadLatestExports(): void {
    const latestExports = this.getLatestExports();
    this._latestExportsSubject.next(latestExports);
  }

  closeLatestExportsPanel(): void {
    this._openLatestExportsPanel.next(false);
  }

  /**
   * Add a new export notification to localStorage
   */
  addExport(notification: NotificationItem): void {
    const latestExports = this.getLatestExports();

    // Add at the beginning
    notification.isViewed = false;
    latestExports.unshift(notification);

    // Keep only MAX_EXPORTS
    if (latestExports.length > this.MAX_EXPORTS) {
      latestExports.splice(this.MAX_EXPORTS);
    }

    this.saveExports(latestExports);
    this._openLatestExportsPanel.next(true);
  }

  /**
   * Get all export notifications
   */
  getLatestExports(): NotificationItem[] {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    if (!stored) {
      return [];
    }

    try {
      const latestExports: NotificationItem[] = JSON.parse(stored);
      return this.filterExpiredExports(latestExports);
    } catch (error) {
      console.error('Error parsing export latestExports from localStorage:', error);
      return [];
    }
  }

  /**
  * Mark a notification as viewed
  */
  markAsViewed(notificationId: string): NotificationItem[] {
    const latestExports = this.getLatestExports();
    const notification = latestExports.find(n => n.notificationId === notificationId);

    if (notification) {
      notification.isViewed = true;
      this.saveExports(latestExports);
    }
    return latestExports;
  }

  /**
   * Remove a notification
   */
  removeExport(notificationId: string): void {
    const latestExports = this.getLatestExports();
    const filtered = latestExports.filter(n => n.notificationId !== notificationId);
    this.saveExports(filtered);
    this._latestExportsSubject.next(filtered);
  }

  /**
   * Clear all notifications
   */
  clearAll(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    this._latestExportsSubject.next([]);
  }

  /**
   * Filter expired notifications
   */
  private filterExpiredExports(latestExports: NotificationItem[]): NotificationItem[] {
    const now = Date.now();
    return latestExports.filter(notification => {
      // Keep notifications that do not have an expiration set
      if (notification.deleteAfter == null) {
        return true;
      }
      // `deleteAfter` is stored in seconds; convert to milliseconds for comparison with Date.now()
      const deleteAfterMs = notification.deleteAfter * 1000;
      // Keep only non-expired notifications
      return deleteAfterMs > now;
    });
  }

  /**
   * Save notifications to localStorage and update observables
   */
  private saveExports(latestExports: NotificationItem[]): void {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(latestExports));
  }

}
